// frontend/src/features/portfolio-diversification-optimizer-pro/DiversificationOptimizerProDashboard.tsx
//
// Step 9 -- master institutional dashboard container for the PRO Portfolio
// Diversification & Optimization engine.
//
// Composes the portfolio specification builder, the executive KPI scorecard,
// and the five analytical pillars behind a sticky pillar-navigation tab bar.
// Handles the full ARQ lifecycle: automatic initial execution on mount,
// animated loading skeletons, a resilient error state with one-click retry,
// and an empty prompt when no analysis has run.
//
// Visually aligned with features/portfolio-drift-auditor-pro (rounded-2xl
// slate/indigo cards, spinner processing state, dashed empty prompt) and fully
// responsive across mobile → ultrawide.

import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  AlertTriangle,
  Compass,
  GitBranch,
  LayoutGrid,
  RefreshCw,
  Scale,
  ShieldAlert,
  Sliders,
} from "lucide-react";

import { useDiversificationProAnalysis } from "./api";
import { ConcentrationAttributionPanel } from "./components/ConcentrationAttributionPanel";
import { CorrelationContagionPanel } from "./components/CorrelationContagionPanel";
import { ExecutiveKpiHeader } from "./components/ExecutiveKpiHeader";
import { FactorExposurePanel } from "./components/FactorExposurePanel";
import { OptimizationSimulatorPanel } from "./components/OptimizationSimulatorPanel";
import { PortfolioProBuilder } from "./components/PortfolioProBuilder";
import { StressTestingPanel } from "./components/StressTestingPanel";
import { buildProRequest, useDiversificationProFormStore } from "./store/useDiversificationProFormStore";
import type { DiversificationProResult, PortfolioProRequest } from "./types";

// Fallback book for the automatic initial execution on mount, used only when
// the re-hydrated builder configuration is infeasible. Mirrors the builder's
// default preset so the dashboard always lands fully populated.
const DEFAULT_REQUEST: PortfolioProRequest = {
  weights: { AAPL: 25, MSFT: 25, NVDA: 20, GLD: 15, TLT: 15 },
  lookback_years: 3,
  min_weight: 0.02,
  max_weight: 0.45,
};

type PillarKey = "correlation" | "concentration" | "factors" | "stress" | "optimization";

interface PillarTab {
  key: PillarKey;
  label: string;
  shortLabel: string;
  icon: React.ComponentType<{ className?: string }>;
}

const PILLAR_TABS: PillarTab[] = [
  { key: "correlation", label: "Correlation & Contagion", shortLabel: "Correlation", icon: GitBranch },
  { key: "concentration", label: "Concentration & Attribution", shortLabel: "Concentration", icon: LayoutGrid },
  { key: "factors", label: "Factor Exposure", shortLabel: "Factors", icon: Compass },
  { key: "stress", label: "Stress & Tail Risk", shortLabel: "Stress", icon: ShieldAlert },
  { key: "optimization", label: "Optimization Simulator", shortLabel: "Optimizer", icon: Scale },
];

// ---------------------------------------------------------------------------
// Pillar tab navigation
// ---------------------------------------------------------------------------
const PillarNav: React.FC<{
  active: PillarKey;
  onChange: (key: PillarKey) => void;
}> = ({ active, onChange }) => (
  <div className="sticky top-0 z-20 -mx-1 overflow-x-auto rounded-2xl border border-slate-200 bg-white/90 p-1.5 shadow-sm backdrop-blur dark:border-slate-800 dark:bg-slate-900/90">
    <div className="flex min-w-max items-center gap-1">
      {PILLAR_TABS.map((tab) => {
        const Icon = tab.icon;
        const isActive = tab.key === active;
        return (
          <button
            key={tab.key}
            type="button"
            onClick={() => onChange(tab.key)}
            className={`flex items-center gap-2 whitespace-nowrap rounded-xl px-3.5 py-2 text-xs font-semibold transition sm:text-sm ${
              isActive
                ? "bg-indigo-600 text-white shadow-sm shadow-indigo-600/25"
                : "text-slate-500 hover:bg-slate-100 hover:text-slate-800 dark:text-slate-400 dark:hover:bg-slate-800 dark:hover:text-slate-200"
            }`}
          >
            <Icon className="h-4 w-4 shrink-0" />
            <span className="hidden sm:inline">{tab.label}</span>
            <span className="sm:hidden">{tab.shortLabel}</span>
          </button>
        );
      })}
    </div>
  </div>
);

// ---------------------------------------------------------------------------
// Results view: executive scorecard + the active pillar panel
// ---------------------------------------------------------------------------
const ResultsView: React.FC<{ result: DiversificationProResult }> = ({ result }) => {
  const [activePillar, setActivePillar] = useState<PillarKey>("optimization");

  const activePanel = useMemo(() => {
    switch (activePillar) {
      case "correlation":
        return (
          <CorrelationContagionPanel
            correlation={result.correlation_analysis}
            tickers={result.tickers}
            hrpOrder={result.hrp_order}
          />
        );
      case "concentration":
        return <ConcentrationAttributionPanel concentration={result.concentration_analysis} />;
      case "factors":
        return <FactorExposurePanel factorExposure={result.factor_exposure} />;
      case "stress":
        return <StressTestingPanel stressTesting={result.stress_testing} />;
      case "optimization":
      default:
        return <OptimizationSimulatorPanel simulations={result.simulations} />;
    }
  }, [activePillar, result]);

  return (
    <div className="space-y-6">
      <ExecutiveKpiHeader data={result} />
      <PillarNav active={activePillar} onChange={setActivePillar} />
      <div>{activePanel}</div>
    </div>
  );
};

// ---------------------------------------------------------------------------
// Loading skeleton
// ---------------------------------------------------------------------------
const LoadingSkeleton: React.FC = () => (
  <div className="space-y-6">
    <div className="flex items-center gap-3 rounded-2xl border border-slate-200 bg-white px-5 py-4 shadow-sm dark:border-slate-800 dark:bg-slate-900/90">
      <div className="h-5 w-5 animate-spin rounded-full border-2 border-indigo-500 border-t-transparent" />
      <div>
        <div className="text-sm font-semibold text-slate-700 dark:text-slate-200">
          Running the 5-pillar institutional engine…
        </div>
        <div className="mt-0.5 text-xs text-slate-400 dark:text-slate-500">
          Ledoit-Wolf shrinkage, Euler risk attribution, factor OLS, Cornish-Fisher tail risk and
          HRP / ERC / Max-Diversification simulations — compiled to native code.
        </div>
      </div>
    </div>

    {/* KPI card skeletons */}
    <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-6">
      {Array.from({ length: 6 }).map((_, i) => (
        <div
          key={i}
          className="h-28 animate-pulse rounded-2xl border border-slate-200 bg-slate-100/80 dark:border-slate-800 dark:bg-slate-800/50"
        />
      ))}
    </div>

    {/* Panel skeletons */}
    <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-3">
      {Array.from({ length: 3 }).map((_, i) => (
        <div
          key={i}
          className="h-48 animate-pulse rounded-2xl border border-slate-200 bg-slate-100/80 dark:border-slate-800 dark:bg-slate-800/50"
        />
      ))}
    </div>
    <div className="h-80 animate-pulse rounded-2xl border border-slate-200 bg-slate-100/80 dark:border-slate-800 dark:bg-slate-800/50" />
  </div>
);

// ---------------------------------------------------------------------------
// Error state
// ---------------------------------------------------------------------------
const ErrorState: React.FC<{ message: string; onRetry: () => void }> = ({ message, onRetry }) => (
  <div className="flex min-h-[360px] flex-col items-center justify-center rounded-2xl border border-rose-500/30 bg-rose-500/5 p-8 text-center">
    <span className="inline-flex h-12 w-12 items-center justify-center rounded-full bg-rose-500/10 text-rose-500 dark:text-rose-400">
      <AlertTriangle className="h-6 w-6" />
    </span>
    <h3 className="mt-4 text-base font-semibold text-slate-800 dark:text-white">
      Analysis could not be completed
    </h3>
    <p className="mt-1 max-w-md text-sm text-slate-500 dark:text-slate-400">{message}</p>
    <button
      type="button"
      onClick={onRetry}
      className="mt-5 inline-flex items-center gap-2 rounded-xl bg-indigo-600 px-4 py-2.5 text-sm font-semibold text-white shadow-lg shadow-indigo-600/25 transition hover:bg-indigo-500"
    >
      <RefreshCw className="h-4 w-4" />
      Retry Analysis
    </button>
  </div>
);

// ---------------------------------------------------------------------------
// Empty state (only shown if the initial auto-run was cleared without result)
// ---------------------------------------------------------------------------
const EmptyState: React.FC = () => (
  <div className="flex min-h-[360px] flex-col items-center justify-center rounded-2xl border-2 border-dashed border-slate-200 bg-slate-50/60 p-8 text-center dark:border-slate-700 dark:bg-slate-900/40">
    <span className="inline-flex h-12 w-12 items-center justify-center rounded-full bg-indigo-500/10 text-indigo-500 dark:text-indigo-400">
      <Sliders className="h-6 w-6" />
    </span>
    <p className="mt-4 max-w-md text-base font-medium text-slate-400 dark:text-slate-500">
      Configure your holdings and box constraints above, then run the institutional diversification
      &amp; optimization engine.
    </p>
  </div>
);

// ---------------------------------------------------------------------------
// Master container
// ---------------------------------------------------------------------------
export const DiversificationOptimizerProDashboard: React.FC = () => {
  const { runAnalysis, result, isLoading, error } = useDiversificationProAnalysis();

  // Track the last submitted request so the error state can retry it verbatim.
  const lastRequestRef = useRef<PortfolioProRequest>(DEFAULT_REQUEST);
  const didAutoRunRef = useRef(false);

  const handleSubmit = (request: PortfolioProRequest) => {
    lastRequestRef.current = request;
    void runAnalysis(request);
  };

  // Automatic initial execution — runs exactly once on mount, against the
  // user's persisted (re-hydrated) configuration so the results match the
  // builder instead of the default book.
  useEffect(() => {
    if (didAutoRunRef.current) return;
    didAutoRunRef.current = true;
    const initial = buildProRequest(useDiversificationProFormStore.getState()) ?? DEFAULT_REQUEST;
    lastRequestRef.current = initial;
    void runAnalysis(initial);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div className="space-y-6">
      <PortfolioProBuilder onSubmit={handleSubmit} isLoading={isLoading} />

      {isLoading ? (
        <LoadingSkeleton />
      ) : error ? (
        <ErrorState message={error} onRetry={() => handleSubmit(lastRequestRef.current)} />
      ) : result ? (
        <ResultsView result={result} />
      ) : (
        <EmptyState />
      )}
    </div>
  );
};
